import { initOAuthManager } from '../utils/oauthManager.js';
import { showLoginRequired } from './imageGeneration.js';
import { requestAiGenerateField } from './generationApi.js';
import { loadDanbooruTags, partitionAppearanceAndOutfit, sanitizePromptTags } from './danbooruTags.js';
import { stripCharacterGenderTags } from './characterGenderTag.js';
import { buildKvDetails, applyKvDetails } from './utils/kvRow.js';
import { openFieldEditModal } from './fieldEditModal.js';
import { getCurrentLanguage, loadCreateCommonTranslations, resolveCreateCommonText } from '../translations.js';

const FIELD_KEY_MAP = {
  'ai-character-prompt': 'characterPrompt',
  'ai-outfit-prompt': 'outfitPrompt',
};

const resolveFieldKey = (targetEl) => {
  const id = targetEl?.id || '';
  return FIELD_KEY_MAP[id] || id || 'field';
};

const getHumanFieldLabel = (targetEl) => {
  const label = targetEl?.closest('label.form-field');
  const labelSpan = label ? label.querySelector('.form-label') : null;
  if (!labelSpan) return 'this field';
  const text = Array.from(labelSpan.childNodes)
    .filter((n) => n.nodeType === Node.TEXT_NODE)
    .map((n) => (n.textContent || '').trim())
    .join(' ')
    .trim();
  return text || 'this field';
};

const getRecruitContextSnapshot = () => {
  const parts = [];
  const request = document.getElementById('ai-request')?.value?.trim();
  if (request) parts.push(`Request: ${request}`);
  const name = document.getElementById('ai-character-name')?.value?.trim();
  if (name) parts.push(`Name: ${name}`);
  const gender = document.getElementById('ai-character-gender')?.value?.trim();
  if (gender) parts.push(`Gender: ${gender}`);
  const appearance = document.getElementById('ai-character-prompt')?.value?.trim();
  if (appearance) parts.push(`Appearance prompt: ${appearance}`);
  const outfit = document.getElementById('ai-outfit-prompt')?.value?.trim();
  if (outfit) parts.push(`Outfit prompt: ${outfit}`);
  const voiceSelect = document.getElementById('ai-character-voice');
  const voiceLabel = voiceSelect?.selectedOptions?.[0]?.textContent?.trim();
  if (voiceLabel && voiceSelect?.value) parts.push(`Voice: ${voiceLabel}`);
  const traits = buildKvDetails();
  if (Object.keys(traits).length) parts.push(`Traits: ${JSON.stringify(traits)}`);
  return parts.join('\n');
};

const safeParseJSON = (text) => {
  if (!text || typeof text !== 'string') return null;
  try { return JSON.parse(text); } catch { return null; }
};

const ensureTrailingComma = (text) => {
  if (!text || typeof text !== 'string') return text;
  const trimmed = text.trim();
  if (!trimmed) return trimmed;
  return trimmed.endsWith(',') ? trimmed : `${trimmed},`;
};

const applySanitizedPromptValue = (targetEl, rawValue) => {
  if (!targetEl || !rawValue) return;

  const fieldId = targetEl.id;
  const outfitEl = document.getElementById('ai-outfit-prompt');

  if (fieldId === 'ai-character-prompt') {
    const existingOutfit = outfitEl?.value?.trim() || '';
    const { appearance, outfit } = partitionAppearanceAndOutfit(rawValue, existingOutfit);
    const visibleAppearance = stripCharacterGenderTags(appearance);
    targetEl.value = visibleAppearance ? ensureTrailingComma(visibleAppearance) : '';
    targetEl.dispatchEvent(new Event('input', { bubbles: true }));
    if (outfitEl && outfit) {
      outfitEl.value = ensureTrailingComma(outfit);
      outfitEl.dispatchEvent(new Event('input', { bubbles: true }));
    }
    return;
  }

  if (fieldId === 'ai-outfit-prompt') {
    const sanitized = sanitizePromptTags(rawValue, {
      suggestionType: 'outfitPrompt',
      forbiddenCategories: [3, 4, 7],
    });
    targetEl.value = sanitized ? ensureTrailingComma(sanitized) : '';
    targetEl.dispatchEvent(new Event('input', { bubbles: true }));
    return;
  }

  targetEl.value = rawValue;
  targetEl.dispatchEvent(new Event('input', { bubbles: true }));
};

const WAND_ICON_HTML = '<i class="fas fa-magic" aria-hidden="true"></i>';
const WAND_SPINNER_HTML = '<i class="fas fa-spinner fa-spin" aria-hidden="true"></i>';

const setWandSpinner = (wandBtn, spinning) => {
  if (!wandBtn) return;
  wandBtn.disabled = spinning;
  wandBtn.innerHTML = spinning ? WAND_SPINNER_HTML : WAND_ICON_HTML;
};

const requestFieldEdit = async (targetEl, wandBtn = null) => {
  if (!targetEl) return;

  const oauthManager = initOAuthManager();
  if (!(await oauthManager.isAuthenticated())) {
    showLoginRequired();
    return;
  }

  const fieldLabel = getHumanFieldLabel(targetEl);
  const instruction = await openFieldEditModal({ fieldLabel });
  if (!instruction) return;

  const field = resolveFieldKey(targetEl);
  const currentValue = targetEl.value || '';
  const context = getRecruitContextSnapshot();

  const prevDisabled = !!targetEl.disabled;
  targetEl.disabled = true;
  setWandSpinner(wandBtn, true);
  const wasAiRunning = window.isAIAssistantRunning;
  window.isAIAssistantRunning = true;

  try {
    await loadDanbooruTags();

    const token = await oauthManager.getValidToken();
    if (!token) throw new Error('Authentication required. Please log in.');

    const payload = await requestAiGenerateField({ field, instruction: instruction.trim(), currentValue, context }, token);

    const nextValue = typeof payload?.value === 'string' ? payload.value.trim() : '';
    if (nextValue) {
      applySanitizedPromptValue(targetEl, nextValue);

      if (targetEl.id === 'ai-character-prompt' && typeof window.updateAllPromptTags === 'function') {
        const gender = document.getElementById('ai-character-gender')?.value || 'female';
        window.updateAllPromptTags(gender);
      }
    }

    const { showSuccess } = await import('../utils/UnifiedNotificationSystem.js');
    showSuccess(resolveCreateCommonText('field_edit.success_updated_field'), { icon: 'check-circle', duration: 2500 });
  } catch (e) {
    if (e.message === 'QUOTA_HANDLED') return;
    const { showError } = await import('../utils/UnifiedNotificationSystem.js');
    showError(resolveCreateCommonText('field_edit.error_field_update', { message: e.message }), { icon: 'exclamation-circle', duration: 5000 });
  } finally {
    window.isAIAssistantRunning = wasAiRunning;
    targetEl.disabled = prevDisabled;
    setWandSpinner(wandBtn, false);
  }
};

const requestKvTableEdit = async (wandBtn = null) => {
  const oauthManager = initOAuthManager();
  if (!(await oauthManager.isAuthenticated())) {
    showLoginRequired();
    return;
  }

  const instruction = await openFieldEditModal({ fieldLabel: resolveCreateCommonText('field_edit.field_traits') });
  if (!instruction) return;

  const table = document.getElementById('step2-kv-table');
  const kvInputs = table ? Array.from(table.querySelectorAll('.kv-key, .kv-value')) : [];
  const prevDisabled = new Map(kvInputs.map((el) => [el, !!el.disabled]));

  setWandSpinner(wandBtn, true);
  kvInputs.forEach((el) => { el.disabled = true; });
  const wasAiRunning = window.isAIAssistantRunning;
  window.isAIAssistantRunning = true;

  try {
    const token = await oauthManager.getValidToken();
    if (!token) throw new Error('Authentication required. Please log in.');

    const payload = await requestAiGenerateField({
      field: 'characterTraits',
      instruction: instruction.trim(),
      currentValue: JSON.stringify(buildKvDetails()),
      context: getRecruitContextSnapshot(),
    }, token);

    const rawValue = typeof payload?.value === 'string' ? payload.value.trim() : '';
    const parsed = rawValue ? safeParseJSON(rawValue) : null;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      throw new Error('AI did not return valid trait data.');
    }

    applyKvDetails(parsed);

    const { showSuccess } = await import('../utils/UnifiedNotificationSystem.js');
    showSuccess(resolveCreateCommonText('field_edit.success_updated_traits'), { icon: 'check-circle', duration: 2500 });
  } catch (e) {
    if (e.message === 'QUOTA_HANDLED') return;
    const { showError } = await import('../utils/UnifiedNotificationSystem.js');
    showError(resolveCreateCommonText('field_edit.error_trait_update', { message: e.message }), { icon: 'exclamation-circle', duration: 5000 });
  } finally {
    window.isAIAssistantRunning = wasAiRunning;
    kvInputs.forEach((el) => { el.disabled = prevDisabled.get(el) || false; });
    setWandSpinner(wandBtn, false);
  }
};

const createKvTableWandButton = () => {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'ai-wand-btn';
  btn.setAttribute('aria-label', resolveCreateCommonText('field_edit.wand_traits_aria'));
  btn.setAttribute('title', resolveCreateCommonText('field_edit.wand_traits_title'));
  btn.innerHTML = WAND_ICON_HTML;

  const handleActivate = (e) => {
    e.preventDefault();
    e.stopPropagation();
    requestKvTableEdit(btn);
  };

  btn.addEventListener('click', handleActivate);
  btn.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    handleActivate(e);
  });

  return btn;
};

const createWandButton = (targetEl) => {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'ai-wand-btn';
  btn.setAttribute('aria-label', resolveCreateCommonText('field_edit.wand_aria'));
  btn.setAttribute('title', resolveCreateCommonText('field_edit.wand_title'));
  btn.innerHTML = WAND_ICON_HTML;

  const handleActivate = (e) => {
    e.preventDefault();
    e.stopPropagation();
    requestFieldEdit(targetEl, btn);
  };

  btn.addEventListener('click', handleActivate);
  btn.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    handleActivate(e);
  });

  return btn;
};

const injectKvTableWand = () => {
  const table = document.getElementById('step2-kv-table');
  if (!table || table.dataset.aiKvWandInjected) return;

  const header = document.createElement('div');
  header.className = 'step2-kv-wand-header';
  header.appendChild(createKvTableWandButton());

  table.parentNode?.insertBefore(header, table);
  table.dataset.aiKvWandInjected = 'true';
};

const injectWands = (fieldIds = []) => {
  fieldIds.forEach((id) => {
    const el = document.getElementById(id);
    if (!el || el.dataset.aiWandInjected) return;
    const label = el.closest('label.form-field');
    const labelSpan = label ? label.querySelector('.form-label') : null;
    if (!label || !labelSpan) return;

    label.classList.add('ai-wand-enabled');
    label.appendChild(createWandButton(el));
    el.dataset.aiWandInjected = 'true';
  });
};

const init = async () => {
  if (!document.body?.classList?.contains('recruit-page')) return;

  await loadCreateCommonTranslations(getCurrentLanguage());

  injectWands([
    'ai-character-prompt',
    'ai-outfit-prompt',
  ]);
  injectKvTableWand();
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
